import type {
  AccountDebt,
  ConsentChannelType,
  ConsentStatus,
  Contact,
  ContactActivitiesResponse,
  ContactCampaignsResponse,
  ContactDebtsResponse,
  ContactIdentitiesResponse,
  ContactIdentityType,
  ContactMemoriesResponse,
  ContactsResponse,
  DebtEventsResponse,
  DebtsResponse,
  IdentityConsentsResponse,
  UpdateDebtPayload,
} from '@/shared/types/contacts';

import { callDelete, callGet, callPatch, callPost, callPut } from './fetcher';

export interface UpsertContactIdentityPayload {
  type: ContactIdentityType;
  value: string;
  verified?: boolean;
}

export interface SetConsentPayload {
  status: ConsentStatus;
  source: string;
  channel_type?: ConsentChannelType | null;
  reason?: string;
}

export const contactsEndpoints = Object.freeze({
  contacts: (params?: URLSearchParams) => {
    const query = params?.toString();
    return `/www/api/v1/contacts${query ? `?${query}` : ''}`;
  },
  contact: (contactId: string) => `/www/api/v1/contacts/${contactId}`,
  memories: (contactId: string, params?: URLSearchParams) => {
    const query = params?.toString();
    return `/www/api/v1/contacts/${contactId}/memories${query ? `?${query}` : ''}`;
  },
  memory: (contactId: string, memoryId: string) =>
    `/www/api/v1/contacts/${contactId}/memories/${memoryId}`,
  identities: (contactId: string) => `/www/api/v1/contacts/${contactId}/identities`,
  identity: (contactId: string, identityId: string) =>
    `/www/api/v1/contacts/${contactId}/identities/${identityId}`,
  campaigns: (contactId: string) => `/www/api/v1/contacts/${contactId}/campaigns`,
  activities: (contactId: string, params?: URLSearchParams) => {
    const query = params?.toString();
    return `/www/api/v1/contacts/${contactId}/activities${query ? `?${query}` : ''}`;
  },
  debts: (contactId: string, params?: URLSearchParams) => {
    const query = params?.toString();
    return `/www/api/v1/contacts/${contactId}/debts${query ? `?${query}` : ''}`;
  },
  consents: (contactId: string, identityId: string) =>
    `/www/api/v1/contacts/${contactId}/identities/${identityId}/consents`,
  debtsList: (params?: URLSearchParams) => {
    const query = params?.toString();
    return `/www/api/v1/debts${query ? `?${query}` : ''}`;
  },
  debt: (debtId: string) => `/www/api/v1/debts/${debtId}`,
  debtEvents: (debtId: string) => `/www/api/v1/debts/${debtId}/events`,
});

export const contactsAPI = Object.freeze({
  listContacts: async (params?: URLSearchParams): Promise<ContactsResponse> => {
    // Always request verbose mode for the list view (memberships, counts, etc.)
    const withVerbose = new URLSearchParams(params ?? undefined);
    withVerbose.set('verbose', 'true');
    return callGet(contactsEndpoints.contacts(withVerbose));
  },

  createContact: async (data: Partial<Contact>): Promise<Contact> =>
    callPost(contactsEndpoints.contacts(), data),

  getContact: async (contactId: string): Promise<Contact> =>
    callGet(contactsEndpoints.contact(contactId)),

  updateContact: async (contactId: string, data: Partial<Contact>): Promise<Contact> =>
    callPut(contactsEndpoints.contact(contactId), data),

  deleteContact: async (contactId: string, force: boolean): Promise<void> =>
    callDelete(`${contactsEndpoints.contact(contactId)}${force ? '?force=true' : ''}`),

  listMemories: async (
    contactId: string,
    params?: URLSearchParams
  ): Promise<ContactMemoriesResponse> => callGet(contactsEndpoints.memories(contactId, params)),

  deleteMemory: async (contactId: string, memoryId: string): Promise<void> =>
    callDelete(contactsEndpoints.memory(contactId, memoryId)),

  listIdentities: async (contactId: string): Promise<ContactIdentitiesResponse> =>
    callGet(contactsEndpoints.identities(contactId)),

  upsertIdentity: async (contactId: string, payload: UpsertContactIdentityPayload): Promise<void> =>
    callPut(contactsEndpoints.identities(contactId), payload),

  deleteIdentity: async (contactId: string, identityId: string): Promise<void> =>
    callDelete(contactsEndpoints.identity(contactId, identityId)),

  listCampaigns: async (contactId: string): Promise<ContactCampaignsResponse> =>
    callGet(contactsEndpoints.campaigns(contactId)),

  listActivities: async (
    contactId: string,
    params?: URLSearchParams
  ): Promise<ContactActivitiesResponse> => callGet(contactsEndpoints.activities(contactId, params)),

  listDebts: async (contactId: string, params?: URLSearchParams): Promise<ContactDebtsResponse> =>
    callGet(contactsEndpoints.debts(contactId, params)),

  listAccountDebts: async (params?: URLSearchParams): Promise<DebtsResponse> =>
    callGet(contactsEndpoints.debtsList(params)),

  deleteDebt: async (debtId: string, force: boolean): Promise<void> =>
    callDelete(`${contactsEndpoints.debt(debtId)}${force ? '?force=true' : ''}`),

  updateDebt: async (debtId: string, payload: UpdateDebtPayload): Promise<AccountDebt> =>
    callPatch(contactsEndpoints.debt(debtId), payload),

  listDebtEvents: async (debtId: string): Promise<DebtEventsResponse> =>
    callGet(contactsEndpoints.debtEvents(debtId)),

  listConsents: async (contactId: string, identityId: string): Promise<IdentityConsentsResponse> =>
    callGet(contactsEndpoints.consents(contactId, identityId)),

  setConsent: async (
    contactId: string,
    identityId: string,
    payload: SetConsentPayload
  ): Promise<void> => callPost(contactsEndpoints.consents(contactId, identityId), payload),
});
